/**
 * CreatorDataAdapter - Maps Creator CMS data to internal Data format.
 *
 * Handles two types of artworks:
 * - ImageTarget: Animation content overlaid on a tracked AR marker image
 * - WorldTarget: 3D panel scenes placed in world space (no image tracking)
 */

import type { Data } from "@core/content/types";
import type {
  CreatorArtwork,
  CreatorFile,
  CreatorPayload,
  MicrositeData,
  PanelArtworkEntry,
} from "./types";
import {
  CreatorFileImageCoverType,
  CreatorFileImageTargetType,
  CreatorFileImageType,
  CreatorFileVideoAlphaType,
  CreatorFileVideoType,
  getArtworkType,
  getArtworks,
  isCollection,
} from "./types";

// =============================================================================
// FILE HELPERS
// =============================================================================

const getFile = (artwork: CreatorArtwork, type: string): CreatorFile | undefined => {
  const requested = type.trim().toLowerCase();
  return artwork.files?.find((f) => f.type?.trim().toLowerCase() === requested);
};

const getArtworkId = (artwork: CreatorArtwork): string => {
  const id = artwork.id?.trim();
  if (!id) {
    throw new Error("Creator artwork is missing an id");
  }
  return id;
};

const getArtworkName = (artwork: CreatorArtwork): string => artwork.name?.trim() || getArtworkId(artwork);

const getImage = (
  artwork: CreatorArtwork,
  imageTypes: (typeof CreatorFileImageTargetType | typeof CreatorFileImageCoverType | typeof CreatorFileImageType)[]
): CreatorFile | undefined => {
  for (const imageType of imageTypes) {
    const image = getFile(artwork, imageType);
    if (image?.path) {
      return image;
    }
  }
  return undefined;
};

const getRequiredImage = (
  artwork: CreatorArtwork,
  imageTypes: (typeof CreatorFileImageTargetType | typeof CreatorFileImageCoverType | typeof CreatorFileImageType)[]
): CreatorFile => {
  const image = getImage(artwork, imageTypes);
  if (!image?.path) {
    throw new Error(`Missing artwork image for artwork ${getArtworkId(artwork)}`);
  }
  return image;
};

const getDescription = (artwork: CreatorArtwork): string | undefined => {
  const trimmed = artwork.description?.trim();
  return trimmed || undefined;
};

const getIcon = (artwork: CreatorArtwork): string | undefined => {
  return getRequiredImage(
    artwork,
    [CreatorFileImageCoverType, CreatorFileImageTargetType, CreatorFileImageType]
  ).path;
};

const getConstructedCover = (
  payload: Pick<CreatorPayload, "files"> & Partial<Pick<CreatorPayload, "artworks">>
): string => {
  const directCover = (
    payload.files?.find((file) => file.type?.trim().toLowerCase() === CreatorFileImageCoverType)?.path ??
    payload.files?.find((file) => file.type?.trim().toLowerCase() === CreatorFileImageTargetType)?.path ??
    payload.files?.find((file) => file.type?.trim().toLowerCase() === CreatorFileImageType)?.path ??
    ""
  );
  if (directCover) return directCover;

  const artworkFallback = payload.artworks
    ?.map((artwork) => getImage(artwork, [CreatorFileImageCoverType, CreatorFileImageTargetType, CreatorFileImageType]))
    .find((file) => Boolean(file?.path));
  return artworkFallback?.path ?? "";
};

// =============================================================================
// IMAGETARGET MAPPER
// =============================================================================

/**
 * Map an ImageTarget artwork to Data format.
 * ImageTarget = animation video playing over a tracked AR marker image.
 */
const mapImageTargetToData = (artwork: CreatorArtwork): Data | null => {
  const artworkId = getArtworkId(artwork);
  const animationFile =
    getFile(artwork, CreatorFileVideoAlphaType) ?? getFile(artwork, CreatorFileVideoType);
  if (!animationFile?.path) {
    return null;
  }

  const targetImage = getRequiredImage(artwork, [CreatorFileImageTargetType, CreatorFileImageType]);

  return {
    id: artworkId,
    type: "image",
    name: getArtworkName(artwork),
    description: getDescription(artwork),
    icon: getIcon(artwork),
    content: [
      {
        type: "video",
        videoHasAlpha: animationFile.type === CreatorFileVideoAlphaType,
        path: animationFile.path,
      },
    ],
    target: {
      targetName: artworkId,
      width: targetImage.width,
      height: targetImage.height,
      imagePath: targetImage.path,
    },
  };
};

// =============================================================================
// WORLDTARGET MAPPER
// =============================================================================

/**
 * Map a WorldTarget artwork to Data format.
 * WorldTarget = 3D panel scene placed in world space (uses sceneConfig for layout).
 * Panels are kept as refs and resolved later when AR content is prepared.
 */
const mapWorldTargetToData = (artwork: CreatorArtwork): Data | null => {
  if (!artwork.sceneConfig) return null;
  const artworkId = getArtworkId(artwork);

  const panelEntry: PanelArtworkEntry = {
    id: artworkId,
    name: getArtworkName(artwork),
    files: artwork.files as PanelArtworkEntry["files"],
    sceneConfig: JSON.stringify(artwork.sceneConfig),
  };

  return {
    id: artworkId,
    type: "scene",
    name: getArtworkName(artwork),
    description: getDescription(artwork),
    icon: getIcon(artwork),
    content: [
      {
        type: "panels",
        path: panelEntry,
      },
    ],
  };
};

// =============================================================================
// ARTWORK MAPPER (routes to appropriate mapper by type)
// =============================================================================

/**
 * Map a single artwork to Data format, automatically detecting the artwork type.
 */
export const mapArtworkToData = (artwork: CreatorArtwork): Data | null => {
  const artworkType = getArtworkType(artwork);
  // console.log(`[CreatorMapper] Mapping artwork "${getArtworkName(artwork)}" as ${artworkType}`);

  switch (artworkType) {
    case "worldtarget":
      return mapWorldTargetToData(artwork);
    case "imagetarget":
    default:
      return mapImageTargetToData(artwork);
  }
};

// =============================================================================
// COLLECTION MAPPERS
// =============================================================================

/**
 * Map all artworks from a CreatorPayload to Data format.
 * Handles both collections (multiple artworks) and single artworks.
 */
export const mapCreatorPayloadToContent = (payload: CreatorPayload): Data[] => {
  return getArtworks(payload)
    .map(mapArtworkToData)
    .filter((item): item is Data => item !== null);
};

/**
 * Extract microsite metadata from a CreatorPayload.
 */
export const mapCreatorPayloadToMicrositeData = (payload: CreatorPayload): MicrositeData => {
  const collection = isCollection(payload) ? payload : null;

  return {
    name: collection?.name ?? payload.name ?? "",
    description: collection?.description ?? payload.description ?? "",
    constructedCover: collection ? getConstructedCover(collection) : getConstructedCover(payload),
  };
};

